import { useSelector } from 'react-redux';
import { classNames, hexToRgba } from '../../../utils/helpers';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Address, Branch, Vehicle } from '@deliveries-app/models';
import BookingTile from './Calendar/BookingTile';
import { GrHostMaintenance } from 'react-icons/gr';
import { RootReducerState } from '../../../store/reducer';

const isTruckSuitable = (vehicle, order) => {
  const bulkItems = order.filter(
    (item) => item.product.product_type === 'bulk'
  );

  const parseNumber = (value) => parseFloat(value);

  if (bulkItems.length === 0) {
    return true;
  } else if (bulkItems.length === 1) {
    const totalWeight = bulkItems.reduce(
      (sum, item) => sum + parseNumber(item.weight) * item.quantity,
      0
    );
    const totalVolume = bulkItems.reduce(
      (sum, item) => sum + parseNumber(item.volume) * item.quantity,
      0
    );

    return (
      totalWeight <= vehicle.max_weight && totalVolume <= vehicle.max_volume
    );
  } else if (bulkItems.length === 2) {
    if (!vehicle.can_split) return false;

    const [firstBulk, secondBulk] = bulkItems;
    const firstBulkVolume = parseNumber(firstBulk.volume) * firstBulk.quantity;
    const secondBulkVolume =
      parseNumber(secondBulk.volume) * secondBulk.quantity;
    const totalWeight = bulkItems.reduce(
      (sum, item) => sum + parseNumber(item.weight) * item.quantity,
      0
    );

    return (
      totalWeight <= vehicle.max_weight &&
      firstBulkVolume <= vehicle.max_volume_first_split &&
      secondBulkVolume <= vehicle.max_volume_second_split
    );
  } else {
    return false; // More than 2 bulk items
  }
};

const Droppable = ({ handleDragDrop, span, isDraggingOver }) => {
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  return (
    <div
      className={classNames(
        '-z-10 row-span-2 my-1 mx-2 flex w-full justify-center rounded-md border border-green-500',
        isDraggingOver ? 'bg-green-100' : 'bg-white'
      )}
      style={{
        height: `${span * 1.5 - 0.75}rem`,
        transition: 'background-color 0.2s',
      }}
      onDragOver={handleDragOver}
    >
      Drop booking
    </div>
  );
};

export interface Event {
  id: number;
  start_time: number;
  branch_id: number;
  vehicle_id: number;
  driver_id: null;
  status: number;
  duration: number;
  branch: Branch;
  vehicle: Vehicle;
  address?: Address;
  booking_id: number;
  span: number;
}

interface BodyProps {
  columns: Event[];
  handleDragEnter: (event: any) => void;
  handleDragDrop: (event: any, index?: any) => void;
  handleHoverOver: (event: any) => void;
  handleSlotClick: (event: any, index: any) => void;
  isUnderMaintenance: (index: number) => any;
  openingHours: any;
  hoverIndex: number;
  scheduleLimit: number;
  draggedBooking: Event | null;
  setDraggedBooking: Dispatch<SetStateAction<Event>>;
  create: boolean;
  duration: number | null;
  showing: string;
  handleShowingTimeline: (a: string) => any;
  timelineRef: any;
  vehicleTime: any;
  fetchBookings: () => void;
  showMaintenanceModal: (id: number) => void;
}

const Body = ({
  columns,
  handleDragEnter,
  handleDragDrop,
  handleHoverOver,
  handleSlotClick,
  openingHours,
  hoverIndex,
  scheduleLimit,
  draggedBooking,
  setDraggedBooking,
  create,
  duration,
  showing,
  handleShowingTimeline,
  timelineRef,
  vehicleTime,
  fetchBookings,
  isUnderMaintenance,
  showMaintenanceModal,
}: BodyProps) => {
  const allProducts = useSelector(
    (state: RootReducerState) => state.dashboard.booking.allProducts
  );
  const branch = useSelector(
    (state: RootReducerState) => state.dashboard.selectedBranch
  );
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  let hourCount = 0;

  const isUnder = useCallback(
    (index: number) => {
      const array = [];

      columns
        .filter((booking) => booking.booking_id !== draggedBooking?.booking_id)
        .forEach((booking) => {
          for (let i = 0; i < booking.span; i++) {
            array.push(
              column(booking.vehicle_id) +
                row(booking.start_time) +
                i * branch.vehicles.length
            );
          }
        });

      return array.includes(index);
    },
    [columns, branch, draggedBooking]
  );

  const isFull = useCallback(
    (index: number) => {
      const counts = {
        true: 0,
        false: 0,
      };

      const row = Math.floor(index / branch.vehicles.length);

      for (let i = 0; i < branch.vehicles.length; i++) {
        counts[`${isUnder(row * branch.vehicles.length + i)}`]++;
      }

      return counts.true >= scheduleLimit;
    },
    [columns, branch.vehicles, scheduleLimit, draggedBooking]
  );

  const column = useCallback(
    (id) => branch.vehicles.findIndex((v) => v.id === id),
    [branch.vehicles]
  );

  const row = useCallback(
    (id) => id * branch.vehicles.length,
    [branch.vehicles]
  );

  const renderCaption = (index: number) => {
    if (isUnderMaintenance(index) && !isUnder(index)) {
      return <span className="text-red-400 opacity-40">Maintenance</span>;
    } else if (isFull(index) && !isUnder(index)) {
      return (
        <span className="text-red-200 opacity-40">Not enough drivers!</span>
      );
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const modalMenus = {
    label: 'Maintenance',
    onClick: showMaintenanceModal,
    icon: <GrHostMaintenance className="mr-2 mt-0.5" />,
  };

  const newBranchVehicles = useMemo(
    () =>
      branch?.vehicles.map((vehicle) => ({
        ...vehicle,
        ...modalMenus,
      })),
    [branch?.vehicles, modalMenus]
  );

  if (!openingHours) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p>Branch is not open today.</p>
      </div>
    );
  }

  return (
    <div
      className="grid gap-x-2"
      style={{
        gridTemplateColumns: `repeat(${branch.vehicles.length}, minmax(18rem, 1fr))`,
      }}
    >
      {newBranchVehicles.map((v) => (
        <div
          key={v.id}
          style={{
            backgroundColor: branch.branch_colour,
          }}
          className={`item shadowntimeData sticky top-40 z-20 my-2 flex ${
            !isTruckSuitable(v, allProducts) && allProducts.length !== 0
              ? 'h-14'
              : 'h-8'
          } w-full flex-col items-center justify-between rounded-md px-2`}
        >
          <div className="flex w-full items-center justify-between">
            <div className="cursor-pointer" onClick={() => v.onClick(v.id)}>
              {v.icon}
            </div>
            <div className="mt-1 flex-grow text-center">
              {v.name}
              <br />
              {(draggedBooking || create) &&
                !isTruckSuitable(v, allProducts) && (
                  <div className="text-red-500">(NOT SUITABLE)</div>
                )}
            </div>
            {v.time_status && (
              <div
                className={`ml-[5px] rounded-lg px-[10px] text-[10px] text-white ${
                  v.time_status.is_late
                    ? 'bg-red-500'
                    : v.time_status.is_ahead
                    ? 'bg-primary'
                    : ''
                }`}
              >
                {v.time_status.diff
                  ? Math.round(v.time_status.diff) + ' M'
                  : null}
              </div>
            )}
          </div>
        </div>
      ))}
      {[...Array(branch.vehicles.length * openingHours.diff)].map(
        (k, index) => (
          <div
            key={index}
            data-index={index}
            className={classNames(
              Math.floor(index / branch.vehicles.length) % 6 === 0
                ? 'border-t'
                : '',
              isFull(index) || isUnderMaintenance(index)
                ? 'bg-red-100'
                : isUnder(index)
                ? ''
                : index == hoverIndex
                ? 'hover:rounded-md'
                : 'hover:rounded-md hover:bg-gray-100',
              'item relative flex h-6 w-full justify-center'
            )}
            style={{
              backgroundColor: isFull(index) ? '#FEF2F2' : '',
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={handleDragEnter}
            onDrop={(e) => handleDragDrop(e, index)}
            onDragStart={() => setDraggingIndex(index)}
            onDragEnd={() => setDraggingIndex(null)}
            onClick={(e) => {
              if (!isFull(index) && !isUnderMaintenance(index))
                handleSlotClick(e, index);
            }}
            onMouseOver={handleHoverOver}
          >
            {index % branch.vehicles.length === 0 &&
              Math.floor(index / branch.vehicles.length) % 12 === 0 && (
                <span className="absolute -left-14 -top-3 items-end justify-end text-right text-sm text-gray-400">
                  {openingHours.start / 12 + hourCount++}:00
                </span>
              )}
            {renderCaption(index)}
            {columns.map((booking) => (
              <>
                {index ==
                column(booking.vehicle_id) + row(booking.start_time) ? (
                  <BookingTile
                    key={booking.id}
                    span={booking.span}
                    setDraggedBooking={setDraggedBooking}
                    bookingId={booking.id}
                    draggedBooking={draggedBooking}
                    openingHours={openingHours}
                    showing={showing}
                    handleShowTimeline={handleShowingTimeline}
                    timelineRef={timelineRef}
                    startTime={booking.start_time}
                  />
                ) : index == hoverIndex &&
                  draggedBooking?.id == booking.booking_id ? (
                  <Droppable
                    key={booking.id}
                    handleDragDrop={(e) => handleDragDrop(e, index)}
                    span={booking.span}
                    isDraggingOver={index === draggingIndex}
                  />
                ) : null}
              </>
            ))}

            {index == hoverIndex &&
              create &&
              duration !== null &&
              !isFull(index) && (
                <Droppable
                  key={999999999}
                  handleDragDrop={(e) => console.log(e, index)}
                  span={Math.ceil(duration / 5)}
                  isDraggingOver={index === draggingIndex}
                />
              )}
          </div>
        )
      )}
      {branch.vehicles.map((v) => (
        <span
          key={v.id}
          className="item sticky top-40 z-20 my-2 flex h-8 w-full items-center justify-center"
        >
          End of day
        </span>
      ))}
    </div>
  );
};

export default Body;
